export enum FileType {
  // media-files
  Image = 'image',
  Icon = 'icon',
  Audio = 'audio',
  Video = 'video',
  // resources
  ResourceImage = 'resourceImage',
  Stroke = 'stroke',
  TtsAudio = 'ttsAudio',
}

export enum ImageVersion {
  Original = 'original',
  Small = 'small',
  Medium = 'medium',
  Large = 'large',
  ExtraLarge = 'extra-large',
  LegacySmall = 'legacy-small', // for IOS 13 or below
  LegacyMedium = 'legacy-medium', // for IOS 13 or below
  LegacyLarge = 'legacy-large', // for IOS 13 or below
  LegacyExtraLarge = 'legacy-extra-large', // for IOS 13 or below
}

export enum AudioVersion {
  source = 'source',
}

export enum VideoVersion {
  source = 'source',
  hls = 'hls',
  WebOptimized = 'web-optimized',
  thumbnail = 'thumbnail',
}

export enum StrokeVersion {
  source = 'source',
}

export enum TtsAudioVersion {
  source = 'source',
}

export enum ImageMimeType {
  Jpeg = 'image/jpeg',
  Png = 'image/png',
  Webp = 'image/webp',
  Heic = 'image/heic',
  Heif = 'image/heif',
}

export enum AudioMimeType {
  Aac = 'audio/aac',
  Mp3 = 'audio/mp3',
  Mpeg = 'audio/mpeg',
  Mpeg3 = 'audio/mpeg3',
}

export enum VideoMimeType {
  Mp4 = 'video/mp4',
  Quicktime = 'video/quicktime',
  Mpegurl = 'application/vnd.apple.mpegurl',
  Jpeg = 'image/jpeg',
}

export enum StrokeMimeType {
  Json = 'application/json',
  SvgXml = 'image/svg+xml',
}

export type MimeType =
  | ImageMimeType
  | AudioMimeType
  | VideoMimeType
  | StrokeMimeType;

interface Attachment {
  contextType: string;
  contextId: string;
  attachmentType: FileType;
  key: string;
  hash: string;
  mimeType: MimeType;
  size: number;
  aspectRatio?: number; // required for image or video
  publicUrls?: Record<string, string>;
}

export interface AttachmentImage extends Attachment {
  attachmentType: FileType.Image;
  mimeType: ImageMimeType;
  aspectRatio: number;
  publicUrls?: Record<ImageVersion, string>;
}

export interface AttachmentAudio extends Attachment {
  attachmentType: FileType.Audio;
  mimeType: AudioMimeType;
  aspectRatio?: never;
  publicUrls?: Record<AudioVersion, string>;
}

export interface AttachmentVideo extends Attachment {
  attachmentType: FileType.Video;
  mimeType: VideoMimeType;
  aspectRatio: number;
  // publicUrls?: Record<VideoVersion, string>;
  publicUrls?: Partial<Record<VideoVersion, string>>;
}

export type AttachmentFile =
  | AttachmentImage
  | AttachmentAudio
  | AttachmentVideo;

export type AttachmentParams<F extends AttachmentFile> = Pick<
  F,
  'key' | 'hash' | 'mimeType' | 'size' | 'aspectRatio'
> & {
  contextType?: never;
  contextId?: never;
  attachmentType?: never;
  publicUrls?: never;
  path?: never;
};

export type AttachmentWithPath<F extends AttachmentFile> = Pick<
  F,
  'key' | 'hash' | 'mimeType' | 'size' | 'aspectRatio'
> & { path?: string };

export const AttachmentImageExample: AttachmentImage = {
  contextType: 'classroom',
  contextId: '54bd2e44-681f-42a8-9e97-092117def75d',
  attachmentType: FileType.Image,
  key: 'carousel0',
  hash: 'uaaxapQ54EaT7rRLZZesmw==',
  mimeType: ImageMimeType.Jpeg,
  size: 222197,
  aspectRatio: 2.2777777777777777,
  publicUrls: {
    original:
      'https://dev-attachments.joyajoy.com/classrooms/default/public/images/00000001/original?hash=uaaxapQ54EaT7rRLZZesmw%3D%3D',
    'extra-large':
      'https://dev-attachments.joyajoy.com/classrooms/default/public/images/00000001/extra-large?hash=uaaxapQ54EaT7rRLZZesmw%3D%3D',
    large:
      'https://dev-attachments.joyajoy.com/classrooms/default/public/images/00000001/large?hash=uaaxapQ54EaT7rRLZZesmw%3D%3D',
    medium:
      'https://dev-attachments.joyajoy.com/classrooms/default/public/images/00000001/medium?hash=uaaxapQ54EaT7rRLZZesmw%3D%3D',
    small:
      'https://dev-attachments.joyajoy.com/classrooms/default/public/images/00000001/small?hash=uaaxapQ54EaT7rRLZZesmw%3D%3D',
    'legacy-extra-large':
      'https://dev-attachments.joyajoy.com/classrooms/default/public/images/00000001/legacy-extra-large?hash=uaaxapQ54EaT7rRLZZesmw%3D%3D',
    'legacy-large':
      'https://dev-attachments.joyajoy.com/classrooms/default/public/images/00000001/legacy-large?hash=uaaxapQ54EaT7rRLZZesmw%3D%3D',
    'legacy-medium':
      'https://dev-attachments.joyajoy.com/classrooms/default/public/images/00000001/legacy-medium?hash=uaaxapQ54EaT7rRLZZesmw%3D%3D',
    'legacy-small':
      'https://dev-attachments.joyajoy.com/classrooms/default/public/images/00000001/legacy-small?hash=uaaxapQ54EaT7rRLZZesmw%3D%3D',
  },
};

export const AttachmentVideoExample: AttachmentVideo = {
  contextType: 'post',
  contextId: '768440c5-3e08-46f2-8545-c550641bc6c8',
  attachmentType: FileType.Video,
  key: 'video-3',
  hash: 'vIMEqRM+b7253ErM6eIrTA==',
  mimeType: VideoMimeType.Mp4,
  size: 4462100,
  aspectRatio: 0.5625,
  publicUrls: {
    hls: 'https://dev-attachments.joyajoy.com/posts/768440c5-3e08-46f2-8545-c550641bc6c8/public/videos/video-3/hls/main.m3u8?hash=vIMEqRM%2Bb7253ErM6eIrTA%3D%3D',
    source:
      'https://dev-attachments.joyajoy.com/posts/768440c5-3e08-46f2-8545-c550641bc6c8/public/videos/video-3/hls/main.m3u8?hash=vIMEqRM%2Bb7253ErM6eIrTA%3D%3D',
  },
};
